import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { BrandService } from '../services/brand.service';

/**
 * ModuleAccessGuard — Defense-in-depth for private-label module restriction.
 *
 * Prevents direct URL navigation to modules not allowed by the current brand.
 * Reads `data.moduleName` from the route config and checks against BrandService.
 *
 * Every brand carries an explicit availableModules allowlist (TEFTER_BRAND
 * excludes the TX modules, TEFTER_TX_BRAND is TX-only, private-label mappings
 * define their own); only allowlisted modules pass. A '*' entry, if a brand
 * ever uses one, allows everything.
 * Blocked routes redirect to the access-denied page.
 */
@Injectable({
  providedIn: 'root',
})
export class ModuleAccessGuard {
  private brandService = inject(BrandService);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
    const moduleName = route.data['moduleName'] as string | undefined;

    // No moduleName defined — allow (e.g. home, profile without moduleName)
    if (!moduleName) {
      return true;
    }

    if (this.brandService.isModuleAvailable(moduleName)) {
      return true;
    }

    // Module not allowed by brand — redirect to access denied
    return this.router.createUrlTree(['/app/access-denied'], {
      queryParams: { reason: 'module', module: moduleName },
    });
  }
}
